import { Alert, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useRef } from "react";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import PageHeader from "src/components/page-header";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import NoneFoundReport from "src/components/none-found-report";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { enqueueSnackbar } from "notistack";
import { DownloadExcelButton, ReportRunner, useDateRangeFilter } from "../runner/report-runner";

type HarshBreakingLine = {
	vehicleUnitNumber: string,
	driverName: string,
	date: string,
	time: string,
	location: string,
	occurredAt: string,
}

type HarshBreakingReportLinesProps = {
  lines: HarshBreakingLine[],
}

const HarshBreakingReportLines = memo(({ lines }: HarshBreakingReportLinesProps) => {
  const { sortedList, sortData } = useSort(lines, "occurredAt", "desc");

  if (!sortedList) {
    return null;
  }

  return <div>
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortData={sortData} sortKey="vehicleUnitNumber">
                Truck
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="driverName">
                Driver
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="occurredAt">
                Date
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="occurredAt">
                Time
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="location">
                Location
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((line, index) =>
              (<TableRow key={index}>
                <TruxTableCell>
                  {line.vehicleUnitNumber}
                </TruxTableCell>
                <TruxTableCell>
                  {line.driverName}
                </TruxTableCell>
                <TruxTableCell>
                  {line.date}
                </TruxTableCell>
                <TruxTableCell>
                  {line.time}
                </TruxTableCell>
                <TruxTableCell>
                  {line.location}
                </TruxTableCell>
              </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  lines?: HarshBreakingLine[],
}

export default function Page() {
  const settings = useSettingsContext();

  const router = useRouter();

  const currentRequest = useRef({} as any);

  const dataLoader = useCallback(() => ({
    lines: (currentRequest.current as any).ran && axios.post(
      endpoints.reports.harshBreaking.data,
      JSON.stringify(currentRequest.current)
    ).then(response => response.data.lines),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loaded,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  // const dateRangeFilter = useDateRangeFilter();
  // const organizationFilter = useOrganizationFilter();
  const dateRangeFilter = useDateRangeFilter();

  const filters = [
    // dateRangeFilter,
    // organizationFilter,
    dateRangeFilter
  ];


  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <Alert severity="info">
       The report is only showing harsh breaking events detected after the 24th of Nov 2025.
      </Alert>;

      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="Harsh Breaking">
          <DownloadExcelButton baseUrl={endpoints.reports.harshBreaking.excel} filters={filters} currentRequest={currentRequest} />
        </PageHeader>

        <ReportRunner
          filters={filters}
          onValidate={(errorMessage) => enqueueSnackbar(errorMessage, {variant: "error"})}
          onRun={(request) => {
            currentRequest.current = request;
            setShouldRefresh(true);
          }}
          loading={loading}
        />

        {data && (data.lines?.length || 0) > 0 && <HarshBreakingReportLines lines={data.lines || []} />}
        {loaded && data && data.lines?.length === 0 && <NoneFoundReport />}
      </DisplayPageData>
    </Container >
  );
}
